import { KindeContext } from "@kinde-oss/kinde-auth-react";
import { useCallback, useContext } from "react";
import { useBrandAuthConfigOptional } from "@brand-auth/BrandAuthProvider";

/**
 * Returns a synchronous click handler that triggers Kinde login for the
 * currently-resolved brand, or `null` when called outside of a
 * {@link BrandAuthProvider} (e.g. unknown application).
 *
 * The handler:
 * - Always passes `orgCode` (see PLAN_SATELLITE_AUTH §10 — relying on
 *   Kinde's default-org behavior has previously dropped users into the
 *   wrong tenant).
 * - Does NOT pre-select a connection. Returning users should land on
 *   Kinde's account-picker so they can log in with whichever provider
 *   they originally signed up with.
 *
 * Returning `null` instead of a no-op lets the caller decide whether to
 * hide the login control entirely when no brand is configured.
 */
export const useBrandLogin = (): (() => void) | null => {
  const config = useBrandAuthConfigOptional();
  const kinde = useContext(KindeContext);

  const onLogin = useCallback((): void => {
    if (!kinde || !config) return;
    void kinde.login({ orgCode: config.kindeOrgCode });
  }, [kinde, config]);

  if (!kinde || !config) return null;
  return onLogin;
};
